import React, { createContext, useContext, useState, useEffect } from 'react';

const StudentContext = createContext();

export const useStudent = () => useContext(StudentContext);

export const StudentProvider = ({ children }) => {
    const [student, setStudent] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [financials, setFinancials] = useState({ totalAmount: 0, paidAmount: 0, balance: 0, payments: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';
    const nonce = config.api?.nonce || config.nonce;
    const headers = { 'X-WP-Nonce': nonce };

    const fetchData = async () => {
        console.log('StudentContext: fetchData starting...');
        try {
            setLoading(true);
            console.log('StudentContext: Fetching customer with headers:', headers);
            const res = await fetch(`${API_BASE}/customer?me=1`, { headers });
            console.log('StudentContext: Customer response status:', res.status);
            if (res.ok) {
                const customers = await res.json();
                console.log('StudentContext: Customers data:', customers);
                if (customers && customers.length > 0) {
                    const s = customers[0];
                    setStudent({
                        id: s.id,
                        name: `${s.first_name} ${s.last_name}`,
                        studentId: s.student_id_number || `SDT-${s.id}`,
                        branch: s.branch_id || s.branch,
                        package: s.package,
                        lessonsRemaining: s.lessons_remaining || 0,
                        stage: s.stage || 'General Training',
                        avatar: s.first_name?.[0] + (s.last_name?.[0] || '')
                    });

                    // Fetch lessons for this student
                    const lessonRes = await fetch(`${API_BASE}/lessons`, { headers });
                    if (lessonRes.ok) {
                        const lessonData = await lessonRes.json();
                        const formattedLessons = lessonData
                            .filter(l => parseInt(l.student_id) === s.id || l.student_id === s.student_id_number)
                            .map(l => ({
                                id: l.id,
                                date: l.start_time,
                                type: l.lesson_type || 'Practical',
                                status: (l.lesson_status || 'Scheduled').charAt(0).toUpperCase() + (l.lesson_status || 'Scheduled').slice(1),
                                instructor: `${l.instructor_first_name || ''} ${l.instructor_last_name || ''}`.trim() || 'Unassigned',
                                notes: l.notes,
                                studentConfirmed: parseInt(l.student_confirmed) === 1,
                                studentCompleted: parseInt(l.student_completed) === 1
                            }));
                        setLessons(formattedLessons);
                    }

                    // Fetch payments for this student
                    const payRes = await fetch(`${API_BASE}/payments`, { headers });
                    if (payRes.ok) {
                        const allPayments = await payRes.json();
                        const studentPayments = allPayments.filter(p =>
                            parseInt(p.customer_id) === s.id ||
                            (s.student_id_number && p.customer_id === s.student_id_number) ||
                            p.student_name === `${s.first_name} ${s.last_name}`
                        );
                        const paidAmount = studentPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);

                        setFinancials({
                            totalAmount: 0,
                            paidAmount,
                            balance: 0,
                            payments: studentPayments
                        });
                    }
                } else {
                    setStudent(null);
                    setLessons([]);
                }
            } else {
                setStudent(null);
                setLessons([]);
            }
        } catch (error) {
            console.error('Error fetching student data:', error);
            setError(error.message);
            setStudent(null);
            setLessons([]);
        } finally {
            setLoading(false);
        }
    };

    const updateLesson = async (lessonId, fields) => {
        try {
            const res = await fetch(`${API_BASE}/lessons/${lessonId}`, {
                method: 'PATCH',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(fields)
            });
            if (res.ok) {
                await fetchData();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating lesson:', error);
            return false;
        }
    };

    useEffect(() => {
        fetchData();
    }, [nonce]);

    const getStats = () => {
        const completed = lessons.filter(l => l.status === 'Completed' || l.studentCompleted).length;
        const total = completed + parseInt(student?.lessonsRemaining || 0);
        const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

        return {
            lessonsCompleted: completed,
            lessonsTotal: total,
            hoursCompleted: completed,
            progress: progress
        };
    };

    return (
        <StudentContext.Provider value={{
            student,
            lessons,
            financials,
            stats: getStats(),
            loading,
            error,
            getNextLesson: () => {
                console.log('StudentContext: Calculating next lesson from:', lessons);
                const next = [...lessons]
                    .filter(l => (l.status === 'Upcoming' || l.status === 'Scheduled') && new Date(l.date) > new Date())
                    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
                console.log('StudentContext: Next lesson found:', next);
                return next || null;
            },
            refetch: fetchData,
            updateLesson
        }}>
            {children}
        </StudentContext.Provider>
    );
};
